/**
 * @copyright Copyright 2020 Epic Systems Corporation
 * @file ErrorBoundary component
 * @author Dustin Gage
 */

import React, { useEffect, ErrorInfo, FunctionComponent } from "react";
import ReactGA from "react-ga";

interface ErrorProps {
	analyticsTrackingId?: string;
	errorPath: string;
}
interface ErrorState {
	hasError: boolean;
	error?: Error;
	stack?: ErrorInfo;
}

export class ErrorBoundary extends React.PureComponent<ErrorProps> {
	public state: ErrorState;

	constructor(props: ErrorProps) {
		super(props);
		this.state = { hasError: false };
	}

	/**
	 * React lifecycle event to capture exceptions and to provide a component-stack-trace
	 * @param error - Caught Error object
	 * @param info - React-specific object containing extra info (like componentStack)
	 */
	public componentDidCatch(error: Error, info: ErrorInfo): void {
		this.setState({
			hasError: true,
			error: error,
			stack: info,
		});

		// log to GA if configured
		if (this.props.analyticsTrackingId !== "") {
			ReactGA.exception({
				description: error.message,
				fatal: true,
			});
		}

		//maybe log to console?
	}

	public render(): React.ReactNode {
		const { hasError, error, stack } = this.state;

		if (hasError) {
			// You can render any custom fallback UI
			return (
				<RedirectOnError errorPath={this.props.errorPath}>
					{__DEV_MODE__ && error && (
						<div className="container container-force-lg">
							<div className="card card-rounded shadow">
								<div className="card-title font-weight-bold text-danger text-center">
									{"Error"}
								</div>
								<div className="card-items">
									<ul>
										{error.stack?.split("\n").map((lvl, ix) => (
											<li key={ix}>{lvl}</li>
										))}
									</ul>
									<div className="card-title font-weight-bold text-danger text-center">
										{"React Stack"}
									</div>
									<div className="card-items">
										<ul>
											{stack?.componentStack
												.split("\n")
												.map((lvl, ix) => lvl && <li key={ix}>{lvl}</li>)}
										</ul>
									</div>
								</div>
							</div>
						</div>
					)}
				</RedirectOnError>
			);
		}
		return this.props.children;
	}
}

const RedirectOnError: FunctionComponent<ErrorProps> = (props) => {
	const { children, errorPath } = props;

	useEffect(() => {
		if (errorPath !== "") {
			window.location.assign(errorPath);
		}
	}, [errorPath]);

	return <>{errorPath === "" ? children : void 0}</>;
};
